import { FC } from "react";
import { StepsProps } from "../../types";
import { Button } from "@/components/ui";
import { FormProvider } from "react-hook-form";
import { CheckCircle2, LockKeyhole } from "lucide-react";
import useStep3 from "./useStep3";
import {
  FormDatePicker,
  FormInput,
  FormPhoneInput,
  FormSectionHeader,
  FormSelect,
  Loader,
} from "@/components";
import {
  ACCOUNT_NUMBER,
  BANK_NAME,
  CURRENCY,
  LEASE_DUE_DATE,
  MONTHLY_DUE_DATE,
  PHONE_1,
  PHONE_2,
  ROUTING_NUMBER,
  YEARLY_DUE_DATE,
  REPORTING_LEVEL,
  DISTRIBUTION_MODE,
  FREQUENCY_FOR_ACTUALS,
  ACTUALS_DAY_OF_MONTH,
  ACTUALS_DATE,
  ACTUALS_DATE_Q1,
  ACTUALS_DATE_Q2,
  ACTUALS_DATE_Q3,
  ACTUALS_DATE_Q4,
  MONTHLY,
  PLAN_ONLY,
  QUARTERLY,
  YEARLY,
  distributionModeOptions,
  frequencyForActualsOptions,
  NONE,
} from "./consts";
import { activeLevels, cn, reportingLevelOptions } from "@/lib/utils";

const Step3: FC<StepsProps> = ({
  onNextClick,
  onCancel,
  isView,
  onPreviousClick,
}) => {
  const {
    form,
    onSubmit,
    requiredFields,
    isUpdatePending,
    isEditDetails,
    id,
    isFinancialInfoLoading,
    isFinancialInfoRefetching,
    monthlyDateKey,
    actualsFrequency,
    distributionMode,
    clearActualsScheduleFields,
    activeThresholdIndex,
    currentReportingLevelLabel,
    onChangeAllocationMode
  } = useStep3({
    onNextClick,
  });
  const { errors } = form.formState;

  return isFinancialInfoLoading || isFinancialInfoRefetching ? (
    <Loader />
  ) : (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[25px]">
          <div>
            <FormSectionHeader>Financial and HOA information</FormSectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
              <FormDatePicker
                name={YEARLY_DUE_DATE}
                control={form.control}
                label="HOA Yearly Billing Due Date"
                placeholder="Please select"
                error={errors[YEARLY_DUE_DATE]?.message}
                isRequired={requiredFields.includes(YEARLY_DUE_DATE)}
                disabled={isView}
              />
              <FormSelect
                key={monthlyDateKey}
                name={MONTHLY_DUE_DATE}
                control={form.control}
                label="HOA Monthly Billing Due Date"
                placeholder="Please select"
                error={errors[MONTHLY_DUE_DATE]?.message}
                isRequired={requiredFields.includes(MONTHLY_DUE_DATE)}
                disabled={isView}
                options={new Array(31)
                  .fill(0)
                  ?.map((_: number, index: number) => ({
                    label: (index + 1)?.toString(),
                    value: (index + 1).toString(),
                  }))}
              />
              <FormDatePicker
                name={LEASE_DUE_DATE}
                control={form.control}
                label="HOA Lease Admin Due Date"
                placeholder="Please select"
                error={errors[LEASE_DUE_DATE]?.message}
                isRequired={requiredFields.includes(LEASE_DUE_DATE)}
                disabled={isView}
              />
              <FormInput
                name={CURRENCY}
                control={form.control}
                label="Currency"
                placeholder="Please enter"
                error={errors[CURRENCY]?.message}
                isRequired={requiredFields.includes(CURRENCY)}
                disabled
              />
              <FormInput
                name={BANK_NAME}
                control={form.control}
                label="Bank Name"
                placeholder="Please enter"
                error={errors[BANK_NAME]?.message}
                isRequired={requiredFields.includes(BANK_NAME)}
                disabled={isView}
              />
              <FormInput
                name={ROUTING_NUMBER}
                control={form.control}
                label="Bank Routing Number"
                placeholder="Please enter"
                error={errors[ROUTING_NUMBER]?.message}
                isRequired={requiredFields.includes(ROUTING_NUMBER)}
                disabled={isView}
                variant="integer"
                maxLength={9}
              />
              <FormInput
                name={ACCOUNT_NUMBER}
                control={form.control}
                label="Bank Account Number"
                placeholder="Please select"
                error={errors[ACCOUNT_NUMBER]?.message}
                isRequired={requiredFields.includes(ACCOUNT_NUMBER)}
                disabled={isView}
                variant="integer"
                maxLength={17}
              />
              <FormPhoneInput
                name={PHONE_1}
                control={form.control}
                label="Zelle Phone 1"
                placeholder="Please select"
                error={errors[PHONE_1]?.message}
                isRequired={requiredFields.includes(PHONE_1)}
                disabled={isView}
              />
              <FormPhoneInput
                name={PHONE_2}
                control={form.control}
                label="Zelle Phone 2"
                placeholder="Please select"
                error={errors[PHONE_2]?.message}
                isRequired={requiredFields.includes(PHONE_2)}
                disabled={isView}
              />
            </div>
          </div>
        </div>
        <div className="mt-[20px] grid grid-cols-1 gap-4 xl:grid-cols-2">
          <div className="bg-white rounded-lg px-[30px] py-[25px] min-h-[321px]">
            <FormSectionHeader>Allocation Configuration</FormSectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-[30px]">
              <FormSelect
                showSearch={true}
                name={REPORTING_LEVEL}
                control={form.control}
                label="Reporting Level"
                placeholder="Please select"
                error={errors[REPORTING_LEVEL]?.message}
                isRequired={requiredFields.includes(REPORTING_LEVEL)}
                options={reportingLevelOptions}
                disabled={isView}
              />
              <FormSelect
                showSearch={false}
                name={DISTRIBUTION_MODE}
                control={form.control}
                label="Allocation Mode"
                placeholder="Please select"
                error={errors[DISTRIBUTION_MODE]?.message}
                isRequired={requiredFields.includes(DISTRIBUTION_MODE)}
                options={distributionModeOptions}
                disabled={isView}
                onChange={onChangeAllocationMode}
              />
              {(distributionMode !== PLAN_ONLY && distributionMode !== NONE) &&  (
                <>
                  <FormSelect
                    showSearch={false}
                    name={FREQUENCY_FOR_ACTUALS}
                    control={form.control}
                    label="Frequency For Actuals"
                    placeholder="Please select"
                    error={errors[FREQUENCY_FOR_ACTUALS]?.message}
                    isRequired={requiredFields.includes(FREQUENCY_FOR_ACTUALS)}
                    options={frequencyForActualsOptions}
                    disabled={isView}
                    onChange={clearActualsScheduleFields}
                  />
                  {actualsFrequency === MONTHLY && (
                    <FormSelect
                      key={`actuals-${monthlyDateKey}`}
                      showSearch={false}
                      name={ACTUALS_DAY_OF_MONTH}
                      control={form.control}
                      label="Day Of Month"
                      placeholder="Select day"
                      error={errors[ACTUALS_DAY_OF_MONTH]?.message}
                      options={new Array(28)
                        .fill(0)
                        .map((_, index: number) => ({
                          label: (index + 1)?.toString(),
                          value: (index + 1)?.toString(),
                        }))}
                      disabled={isView}
                    />
                  )}
                  {actualsFrequency === QUARTERLY && (
                    <>
                      <FormDatePicker
                        name={ACTUALS_DATE_Q1}
                        control={form.control}
                        label="Date Q1"
                        placeholder="Select"
                        error={errors[ACTUALS_DATE_Q1]?.message}
                        disabled={isView}
                      />
                      <FormDatePicker
                        name={ACTUALS_DATE_Q2}
                        control={form.control}
                        label="Date Q2"
                        placeholder="Select"
                        error={errors[ACTUALS_DATE_Q2]?.message}
                        disabled={isView}
                      />
                      <FormDatePicker
                        name={ACTUALS_DATE_Q3}
                        control={form.control}
                        label="Date Q3"
                        placeholder="Select"
                        error={errors[ACTUALS_DATE_Q3]?.message}
                        disabled={isView}
                      />
                      <FormDatePicker
                        name={ACTUALS_DATE_Q4}
                        control={form.control}
                        label="Date Q4"
                        placeholder="Select"
                        error={errors[ACTUALS_DATE_Q4]?.message}
                        disabled={isView}
                      />
                    </>
                  )}
                  {actualsFrequency === YEARLY && (
                    <FormDatePicker
                      name={ACTUALS_DATE}
                      control={form.control}
                      label="Date"
                      placeholder="Select"
                      error={errors[ACTUALS_DATE]?.message}
                      disabled={isView}
                    />
                  )}
                </>
              )}
            </div>
          </div>
          <div className="bg-white rounded-lg px-[30px] py-[25px] min-h-[290px] ">
            <FormSectionHeader>Active Level Path</FormSectionHeader>
            <div className="space-y-4 text-[14px] text-medium">
              {
                activeLevels?.map((level: any, index: number) => {
                  const isUnlocked = index <= activeThresholdIndex;
                  return (
                    <div className={cn("flex items-center gap-2", isUnlocked ? "text-gray-700" : "text-gray-400")}>
                      {isUnlocked ? <CheckCircle2 className="h-5 w-5 fill-green-600 stroke-white" /> : <LockKeyhole className="h-5 w-5 text-primary" />}
                      <span className="font-medium">
                        Level {index + 1}: {level?.label || ""} {isUnlocked ? "" : "(DISABLED)"}
                      </span>
                    </div>
                  )
                })
              }
            </div>
            <div className="mt-5 rounded-md border border-primary bg-blue-50 px-3 py-3 text-base font-[500] leading-7 text-gray-700 text-[14px] text-medium">
              Since reporting level is {currentReportingLevelLabel}, the system will stop distribution
              at {currentReportingLevelLabel} level.
            </div>
          </div>
        </div>
        <div className="flex flex-row justify-between items-center  py-3">
          {!id ? (
            <Button variant="outline" type="button" onClick={onPreviousClick}>
              Previous
            </Button>
          ) : (
            <div></div>
          )}
          <div className="flex flex-row justify-end gap-3  py-3">
            <Button variant="outline" type="button" onClick={onCancel}>
              Cancel
            </Button>
            {!isView && (
              <Button type="submit">
                {isUpdatePending && <Loader variant="secondary" />}
                {isEditDetails ? "Save" : "Save & Next"}
              </Button>
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default Step3;
